import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import type { InvoicePrefSubscriber } from "../../api/invoicePrefs";
import { getDiagnosis, normalizeMac, onuDevices, renameDevice, type DeviceRenameResult } from "../../api/subscriberDevice";

/**
 * Devices › Change ONU MAC (owner 2026-10-17, was PHP onuchange.php): the
 * subscriber's ONU was replaced, so sign-in moves to the new box's MAC. The
 * plan, receipts and session history move with it; nothing is lost.
 */
export function SubscriberOnuDialog({ ns, subscriber, onClose }: { ns: string; subscriber: InvoicePrefSubscriber | null; onClose: () => void }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const devices = useQuery({
    queryKey: ["diagnose", ns, subscriber?.accountid],
    enabled: Boolean(subscriber && identity.token && ns),
    queryFn: ({ signal }) => getDiagnosis(identity, ns, subscriber?.accountid ?? "", signal),
    retry: false,
    staleTime: 0,
  });
  const onus = devices.data ? onuDevices(devices.data) : [];
  const [picked, setPicked] = useState("");
  const [typed, setTyped] = useState("");
  const [done, setDone] = useState<DeviceRenameResult | null>(null);
  const from = picked || (onus.length === 1 ? onus[0]!.username : "");
  const to = normalizeMac(typed);
  const same = to !== null && to === from.toUpperCase();

  const mutation = useMutation({
    mutationFn: () => renameDevice(identity, ns, subscriber?.accountid ?? "", from, typed),
    onSuccess: async (r) => {
      setDone(r);
      await qc.invalidateQueries({ queryKey: ["diagnose", ns] });
      await qc.invalidateQueries({ queryKey: ["invoiceprefs", ns] });
      await qc.invalidateQueries({ queryKey: ["rechargeoptions", ns] });
    },
  });

  const close = () => {
    setPicked("");
    setTyped("");
    setDone(null);
    mutation.reset();
    onClose();
  };
  const who = subscriber ? subscriber.name || subscriber.username || subscriber.accountid : "";
  const err = mutation.error;

  return (
    <Dialog open={subscriber !== null} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>Change Device MAC · {who}</DialogTitle>
      <DialogContent>
        {devices.isPending && <CircularProgress size={24} />}
        {devices.error && <Alert severity="error">{devices.error instanceof Error ? devices.error.message : String(devices.error)}</Alert>}
        {done ? (
          <Alert severity="success">
            The ONU now signs in as <code>{done.to}</code> (was <code>{done.from}</code>). Its plan, receipts and session history moved with it.
            Restart the new ONU if it is not online in a minute.
            {done.history_recorded === false && " The change could not be added to User diagnose's ONU history; tell support."}
          </Alert>
        ) : (
          devices.data && (
            <>
              {onus.length === 0 && <Alert severity="info">This subscriber has no ONU: nothing to change. Mobile sign-ins have no MAC.</Alert>}
              {onus.length > 1 && (
                <RadioGroup value={from} onChange={(e) => setPicked(e.target.value)} sx={{ mb: 1 }}>
                  {onus.map((o) => (
                    <FormControlLabel key={o.username} value={o.username} control={<Radio size="small" />} label={<code>{o.username}</code>} />
                  ))}
                </RadioGroup>
              )}
              {onus.length === 1 && (
                <Typography sx={{ mb: 2 }}>
                  Current ONU: <code>{from}</code>
                </Typography>
              )}
              {onus.length > 0 && (
                <TextField
                  label="New ONU MAC address"
                  placeholder="AA:BB:CC:DD:EE:FF"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  fullWidth
                  size="small"
                  autoFocus
                  error={typed !== "" && (to === null || same)}
                  helperText={
                    typed === ""
                      ? "From the label on the new ONU."
                      : to === null
                        ? "Twelve hex digits, e.g. AA:BB:CC:DD:EE:FF."
                        : same
                          ? "That is the current MAC."
                          : `Will be saved as ${to}.`
                  }
                  inputProps={{ spellCheck: false, autoCapitalize: "characters" }}
                />
              )}
            </>
          )
        )}
        {err && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {err instanceof Error ? err.message : String(err)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {done ? (
          <Button variant="contained" onClick={close}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={close}>Cancel</Button>
            <Button variant="contained" disabled={!from || to === null || same || mutation.isPending} onClick={() => mutation.mutate()}>
              {mutation.isPending ? "Changing…" : "Change"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
